import React from 'react';
import { Box, VStack, HStack, Text, Button, Badge } from '@chakra-ui/react';
import { BottomSheet } from '@/components/ui/custom/bottom-sheet';
import { StoreProduct, SelectedProduct } from '@/types/payment';

interface ProductSelectBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  products: StoreProduct[];
  selectedProducts: SelectedProduct[];
  onChangeQuantity: (product: StoreProduct, delta: number) => void;
  onConfirm: () => void;
}

/** productStatus に応じたラベルと色を返す */
const getStatusLabel = (
  status: StoreProduct['productStatus']
): { label: string; color: string } | null => {
  if (status === '01') return { label: '一時休止', color: 'orange' };
  if (status === '02') return { label: '入荷未定', color: 'red' };
  return null;
};

export const ProductSelectBottomSheet: React.FC<ProductSelectBottomSheetProps> = ({
  isOpen,
  onClose,
  products,
  selectedProducts,
  onChangeQuantity,
  onConfirm,
}) => {
  const getQuantity = (productId: string): number => {
    const found = selectedProducts.find(sp => sp.product.id === productId);
    return found ? found.quantity : 0;
  };

  const totalAmount = selectedProducts.reduce((sum, sp) => sum + sp.product.price * sp.quantity, 0);

  const hasSelected = selectedProducts.length > 0;

  return (
    <BottomSheet
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      title="商品を選択"
      description="商品を選択してください"
      height="80vh"
    >
      <VStack gap={0} w="full" align="stretch">
        {products.map((product, index) => {
          const quantity = getQuantity(product.id);
          const isUnavailable = product.productStatus !== '00';
          const statusLabel = getStatusLabel(product.productStatus);
          return (
            <Box key={product.id}>
              <HStack justify="space-between" py={3} align="center">
                <VStack align="start" gap={0} flex={1}>
                  <HStack gap={2} align="center">
                    <Text fontWeight="medium" color={isUnavailable ? 'gray.400' : 'gray.800'}>
                      {product.name}
                    </Text>
                    {statusLabel && (
                      <Badge colorPalette={statusLabel.color} size="sm">
                        {statusLabel.label}
                      </Badge>
                    )}
                  </HStack>
                  {product.description && (
                    <Text fontSize="xs" color={isUnavailable ? 'gray.300' : 'gray.500'}>
                      {product.description}
                    </Text>
                  )}
                  <Text
                    fontSize="sm"
                    color={isUnavailable ? 'gray.400' : 'teal.600'}
                    fontWeight="bold"
                  >
                    ฿{product.price.toLocaleString()}
                  </Text>
                </VStack>
                <HStack gap={2} align="center">
                  <Button
                    size="sm"
                    variant="outline"
                    colorPalette="teal"
                    onClick={() => onChangeQuantity(product, -1)}
                    disabled={quantity === 0 || isUnavailable}
                    minW="36px"
                    h="36px"
                    p={0}
                  >
                    −
                  </Button>
                  <Text
                    w="28px"
                    textAlign="center"
                    fontWeight="semibold"
                    fontSize="md"
                    color={isUnavailable ? 'gray.400' : undefined}
                  >
                    {quantity}
                  </Text>
                  <Button
                    size="sm"
                    colorPalette="teal"
                    onClick={() => onChangeQuantity(product, 1)}
                    disabled={isUnavailable}
                    minW="36px"
                    h="36px"
                    p={0}
                  >
                    ＋
                  </Button>
                </HStack>
              </HStack>
              {index < products.length - 1 && <Box h="1px" bg="gray.200" />}
            </Box>
          );
        })}

        {hasSelected && (
          <Box mt={4} p={3} bg="teal.50" borderRadius="md">
            <HStack justify="space-between">
              <Text fontWeight="bold" color="gray.700">
                合計
              </Text>
              <Text fontWeight="bold" color="teal.700" fontSize="lg">
                ฿{totalAmount.toLocaleString()}
              </Text>
            </HStack>
          </Box>
        )}

        <Button
          w="full"
          size="lg"
          colorPalette="teal"
          mt={4}
          onClick={onConfirm}
          disabled={!hasSelected}
        >
          {hasSelected ? `選択する（฿${totalAmount.toLocaleString()}）` : '商品を選んでください'}
        </Button>
      </VStack>
    </BottomSheet>
  );
};
